import type { TagPropsInterface } from '@zillow/constellation-10';
import { logError } from '@zg-rentals/log-error';
import { AccountViewListingStatus } from '@zg-rentals/ts-rental-manager-api-client';
import { APPLICATION_STATUS_TYPE } from '../constants/ApplicationStatusConstants';
import { LEASE_STATUS } from '../constants/LeaseConstants';
import { LISTING_SUMMARY_DISPLAY_STATUS } from '../constants/ListingSummaryConstants';
import { PAYMENT_STATUS_TYPE } from '../constants/PaymentConstants';
import PropertyConstants from '../constants/PropertyConstants';

// Extract Tag tone type from Constellation v10
export type TagTone = TagPropsInterface['tone'];

const STATUS_TAG_TYPE = {
  // aqua - custom branding color
  AQUA: 'aqua',
  // critical - semantic tone for errors/critical status
  ERROR: 'critical',
  // gray - semantic tone for neutral/inactive status
  GRAY: 'gray',
  // info - semantic tone for informational status
  INFO: 'info',
  // success - semantic tone for success/positive status
  SUCCESS: 'success',
  // transparent - semantic tone for neutral/inactive status
  TRANSPARENT: 'transparent',
  // warning - semantic tone for warning/caution status
  WARNING: 'warning',
};

// map for /api/v1/users/properties/feedListingManagement/getListingSources
// map for /api/v2/users/properties/details (syndication.overview.status)
// map for /api/v2/users/properties/list
const SYNDICATION_OVERVIEW_STATUS = {
  [PropertyConstants.SYNDICATION_OVERVIEW_STATUS.INACTIVE]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
  [PropertyConstants.SYNDICATION_OVERVIEW_STATUS.PUBLISHING]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.WARNING,
  },
  [PropertyConstants.SYNDICATION_OVERVIEW_STATUS.PUBLISHED]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.SUCCESS,
  },
  [PropertyConstants.SYNDICATION_OVERVIEW_STATUS.PARTIALLY_PUBLISHED.toLowerCase()]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.SUCCESS,
  },
  [PropertyConstants.SYNDICATION_OVERVIEW_STATUS.DECLINED]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.ERROR,
  },
  [PropertyConstants.SYNDICATION_OVERVIEW_STATUS.WAITLISTED]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
};

// map for api/v1/users/accountView/properties/list (some tag appearances are returned)
const LISTING_DISPLAY_STATUS = {
  [AccountViewListingStatus.PUBLISHED.toLowerCase()]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.SUCCESS,
  },
  default: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
};

// map for /api/v1/users/properties/summary/listing
const LISTING_SUMMARY = {
  [LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
  [LISTING_SUMMARY_DISPLAY_STATUS.PUBLISHING]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.INFO,
  },
  [LISTING_SUMMARY_DISPLAY_STATUS.PUBLISHED]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.SUCCESS,
  },
  [LISTING_SUMMARY_DISPLAY_STATUS.PARTIALLY_PUBLISHED]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.SUCCESS,
  },
  [LISTING_SUMMARY_DISPLAY_STATUS.DECLINED]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.ERROR,
  },
  [LISTING_SUMMARY_DISPLAY_STATUS.WAITLISTED]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
};

const MAINTENANCE_TASKS = {
  closed: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
  inProgress: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.AQUA,
  },
  open: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.WARNING,
  },
  completed: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.SUCCESS,
  },
};

// map for /api/v2/users/properties/details/overview
const PROPERTY_DETAILS_OVERVIEW = {
  // eslint-disable-next-line constellation/word-list
  'off-market': {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.TRANSPARENT,
  },
  archived: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.TRANSPARENT,
  },
  'listed for rent': {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.SUCCESS,
  },
  'occupied by tenants': {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.TRANSPARENT,
  },
  'unable to publish': {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.WARNING,
  },
};

// map for /api/v1/users/properties/summary/leases
const LEASE_SUMMARY = {
  [LEASE_STATUS.ACTIVE]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.SUCCESS,
  },
  [LEASE_STATUS.EXPIRED]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
  [LEASE_STATUS.INCOMPLETE_DRAFT]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.WARNING,
  },
  [LEASE_STATUS.COMPLETE_DRAFT]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.WARNING,
  },
  [LEASE_STATUS.SIGNING_CANCELLED]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
  [LEASE_STATUS.SIGNING_COMPLETE]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.SUCCESS,
  },
  [LEASE_STATUS.SIGNING_IN_PROGRESS]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.AQUA,
  },
  [LEASE_STATUS.SIGNING_OFFLINE]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.SUCCESS,
  },
  [LEASE_STATUS.AWAITING_YOUR_SIGNATURE]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.WARNING,
  },
};

// map for /api/v1/users/properties/summary/payments
const PAYMENT_SUMMARY = {
  [PAYMENT_STATUS_TYPE.INFO]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
  [PAYMENT_STATUS_TYPE.NEUTRAL]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
  [PAYMENT_STATUS_TYPE.IN_PROGRESS]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.WARNING,
  },
  [PAYMENT_STATUS_TYPE.SUCCESS]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.SUCCESS,
  },
  [PAYMENT_STATUS_TYPE.WARN]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.WARNING,
  },
  [PAYMENT_STATUS_TYPE.CAUTION]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.ERROR,
  },
};

// map for /api/v1/users/properties/summary/applications
const INTERESTED_RENTERS = {
  [APPLICATION_STATUS_TYPE.ACCEPTED]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.SUCCESS,
  },
  [APPLICATION_STATUS_TYPE.WITHDRAWN]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
  [APPLICATION_STATUS_TYPE.COMPLETED]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.SUCCESS,
  },
  [APPLICATION_STATUS_TYPE.PENDING]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.WARNING,
  },
  [APPLICATION_STATUS_TYPE.INVITED]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
  received: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.SUCCESS,
  },
  [APPLICATION_STATUS_TYPE.IN_PROGRESS]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.WARNING,
  },
  [APPLICATION_STATUS_TYPE.INVITED_TO_APPLY]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
  [APPLICATION_STATUS_TYPE.CONDITIONALLY_APPROVED]: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.WARNING,
  },
};

const SELF_SERVE_ONBOARDING = {
  actionrequired: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.ERROR,
  },
  propertyinfosubmitted: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.SUCCESS,
  },
  underreview: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.ERROR,
  },
};

// map /apps/satellite-landlord-web/src/components/StatusLabel/index.tsx#22-62
const INTERESTED_RENTERS_LEAD_MANAGEMENT = {
  statuslabelapplicationdeclined: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
  statuslabelapplicationapproved: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.SUCCESS,
  },
  statuslabelapplicationreceived: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.SUCCESS,
  },
  statuslabelapplicationrequested: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
  statuslabelapplicationwithdrawn: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
  statuslabelawaitingyoursignature: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.WARNING,
  },
  statuslabelinquired: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
  statuslabelinvitedtoapply: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
  statuslabelleasecanceled: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
  statuslabellease: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.SUCCESS,
  },
  statuslabelpayments: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
  statuslabelphoneinquired: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
  statusresponseoverdue: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.WARNING,
  },
  statuslabelsigninginprogress: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.INFO,
  },
  statuslabeltour: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.INFO,
  },
  statuslabeltoured: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
  statuslabeltourrequested: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.GRAY,
  },
  statuslabelapplicationconditionallyapproved: {
    STATUS_TAG_TYPE: STATUS_TAG_TYPE.WARNING,
  },
};

const STATUS_KEY = {
  INTERESTED_RENTERS: 'INTERESTED_RENTERS',
  INTERESTED_RENTERS_LEAD_MANAGEMENT: 'INTERESTED_RENTERS_LEAD_MANAGEMENT',
  LEASE_SUMMARY: 'LEASE_SUMMARY',
  LISTING_SUMMARY: 'LISTING_SUMMARY',
  LISTING_DISPLAY_STATUS: 'LISTING_DISPLAY_STATUS',
  PAYMENT_SUMMARY: 'PAYMENT_SUMMARY',
  PROPERTY_DETAILS_OVERVIEW: 'PROPERTY_DETAILS_OVERVIEW',
  SYNDICATION_OVERVIEW_STATUS: 'SYNDICATION_OVERVIEW_STATUS',
  MAINTENANCE_TASKS: 'MAINTENANCE_TASKS',
  SELF_SERVE_ONBOARDING: 'SELF_SERVE_ONBOARDING',
};

const LOOKUP = {
  [STATUS_KEY.INTERESTED_RENTERS]: INTERESTED_RENTERS,
  [STATUS_KEY.INTERESTED_RENTERS_LEAD_MANAGEMENT]: INTERESTED_RENTERS_LEAD_MANAGEMENT,
  [STATUS_KEY.LEASE_SUMMARY]: LEASE_SUMMARY,
  [STATUS_KEY.MAINTENANCE_TASKS]: MAINTENANCE_TASKS,
  [STATUS_KEY.LISTING_SUMMARY]: LISTING_SUMMARY,
  [STATUS_KEY.PAYMENT_SUMMARY]: PAYMENT_SUMMARY,
  [STATUS_KEY.PROPERTY_DETAILS_OVERVIEW]: PROPERTY_DETAILS_OVERVIEW,
  [STATUS_KEY.SYNDICATION_OVERVIEW_STATUS]: SYNDICATION_OVERVIEW_STATUS,
  [STATUS_KEY.SELF_SERVE_ONBOARDING]: SELF_SERVE_ONBOARDING,
  [STATUS_KEY.LISTING_DISPLAY_STATUS]: LISTING_DISPLAY_STATUS,
};

const getTagTypeByKey = (key: string, status: string): TagTone => {
  let lowerCaseStatus;

  try {
    // validation
    if (!key) {
      throw new Error('key is not defined');
    }

    if (!status) {
      throw new Error('status is not defined');
    }

    // do lookup
    const statusMap = LOOKUP[key];

    if (!statusMap) {
      logError({
        error: new Error(`Status map not found for key: ${key}`),
        errorType: 'StatusTagError',
        context: {
          key,
          status,
          details: `No status map found for key “${key}”. Returning default tone “${STATUS_TAG_TYPE.GRAY}”.`,
        },
      });
      return STATUS_TAG_TYPE.GRAY as TagTone;
    }

    lowerCaseStatus = status.toLowerCase();

    // @ts-expect-error - statusMap indexing with dynamic key
    const tagType = (statusMap[lowerCaseStatus] || statusMap.default)?.STATUS_TAG_TYPE;

    // If lookup fails (no match and no default), log and return default
    if (!tagType) {
      logError({
        error: new Error(`Status not found in map for key: ${key}, status: ${lowerCaseStatus}`),
        errorType: 'StatusTagError',
        context: {
          key,
          status: lowerCaseStatus,
          details: `No matching status found for “${lowerCaseStatus}” in map “${key}” and no default exists. Returning default tone “${STATUS_TAG_TYPE.GRAY}”.`,
        },
      });
      return STATUS_TAG_TYPE.GRAY as TagTone;
    }

    return tagType as TagTone;
  } catch (error) {
    logError({
      error,
      errorType: 'StatusTagError',
      context: {
        key,
        status: lowerCaseStatus,
        details: `An error occurred while looking up status tag. Returning default tone “${STATUS_TAG_TYPE.GRAY}”. Check undefined status “${status}”, and lowerCaseStatus “${lowerCaseStatus}” against the associated map “${key}”`,
      },
    });

    // Always return a valid tone - required by the Tag component
    return STATUS_TAG_TYPE.GRAY as TagTone;
  }
};

export { STATUS_KEY, STATUS_TAG_TYPE, getTagTypeByKey };
